import { Trash2, Pencil, UserPlus, RefreshCw, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { StatusToggle } from './StatusToggle';
import { PlayerStatusBadge } from './PlayerStatusBadge';
import { playerDisplayName } from './player-utils';
import type { Player, PlayerStatus } from '@swiss-magic/shared-types';

// Sticky bottom action bar for PlayerDetailPanel. Shape depends on
// edit mode:
//   editing: Save + Cancel
//   viewing: StatusToggle (when allowed) + status badge (when terminal) +
//            Unblock (when blocked) + Edit + Invite/Refresh + Delete

interface Props {
  player: Player;
  canEdit: boolean;
  isRegistrationOpen: boolean;
  editing: boolean;
  onEnterEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete: () => void;
  onStatusChange: (player: Player, status: PlayerStatus) => void;
  onInvite: (player: Player) => void;
  onRefreshFromProfile: (player: Player) => void;
  onMessage: (player: Player) => void;
  onUnblock: (player: Player) => void;
}

export function PlayerDetailFooter({
  player,
  canEdit,
  isRegistrationOpen,
  editing,
  onEnterEdit,
  onSave,
  onCancel,
  onDelete,
  onStatusChange,
  onInvite,
  onRefreshFromProfile,
  onMessage,
  onUnblock,
}: Props) {
  const { t } = useTranslation('director');

  if (editing) {
    return (
      <div className="border-t p-4 space-y-2">
        <div className="flex gap-2">
          <Button
            className="flex-1"
            data-testid="detail-save-button"
            onClick={onSave}
          >
            {t('players_page.detail_panel.save_button')}
          </Button>
          <Button
            variant="outline"
            className="flex-1"
            data-testid="detail-cancel-button"
            onClick={onCancel}
          >
            {t('players_page.detail_panel.cancel_button')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="border-t p-4 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {canEdit &&
          player.status !== 'withdrawn' &&
          player.status !== 'eliminated' &&
          player.status !== 'blocked' && (
            <div className="[&_button]:h-9 [&_button]:w-9">
              <StatusToggle
                status={player.status}
                onChange={(s) => onStatusChange(player, s)}
                testIdPrefix={`player-${playerDisplayName(player)}`}
              />
            </div>
          )}
        {(player.status === 'withdrawn' ||
          player.status === 'eliminated' ||
          player.status === 'blocked') && (
          <span data-testid={`player-${playerDisplayName(player)}-status-badge`}>
            <PlayerStatusBadge status={player.status} />
          </span>
        )}
        {canEdit && player.status === 'blocked' && (
          <Button
            variant="outline"
            size="sm"
            data-testid="detail-unblock-button"
            onClick={() => onUnblock(player)}
          >
            {t('players_page.detail_panel.unblock_button')}
          </Button>
        )}
        <div className="flex flex-1 gap-2">
          {canEdit && (
            <Button
              className="flex-1"
              data-testid="detail-edit-button"
              onClick={onEnterEdit}
            >
              <Pencil className="mr-1 h-4 w-4" />
              {t('players_page.detail_panel.edit_button')}
            </Button>
          )}
          {!player.profileUserId && (
            <Button
              variant="outline"
              size="icon"
              className="shrink-0"
              data-testid={`invite-player-${playerDisplayName(player)}`}
              title={t('players_page.detail_panel.invite_player_title')}
              aria-label={t('players_page.detail_panel.invite_player_aria')}
              onClick={() => onInvite(player)}
            >
              <UserPlus className="h-4 w-4" />
            </Button>
          )}
          {player.profileUserId && (
            <Button
              variant="outline"
              size="icon"
              className="shrink-0"
              data-testid={`refresh-player-${playerDisplayName(player)}`}
              title={t('players_page.detail_panel.refresh_title')}
              aria-label={t('players_page.detail_panel.refresh_aria')}
              onClick={() => onRefreshFromProfile(player)}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
          {player.profileUserId && (
            <Button
              variant="outline"
              size="icon"
              className="shrink-0"
              data-testid={`message-player-${playerDisplayName(player)}`}
              title={t('players_page.detail_panel.message_player_title')}
              aria-label={t('players_page.detail_panel.message_player_aria')}
              onClick={() => onMessage(player)}
            >
              <Send className="h-4 w-4" />
            </Button>
          )}
          {isRegistrationOpen && player.status !== 'withdrawn' && player.status !== 'eliminated' && (
            <Button
              variant="destructive"
              data-testid="detail-delete-button"
              onClick={onDelete}
            >
              <Trash2 className="mr-1 h-4 w-4" />
              {t('players_page.detail_panel.delete_button')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
